import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import Button from "../../components/UI/Button";
import Input from "../../components/UI/Input";
import logo from "../../assets/logoProd.png";
import logoLight from "../../assets/logoProdLight.png";
import { useIsMobile } from "../../App";
import analyticsService from "../../services/AnalyticsService";

const RegisterScreen = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    acceptTerms: true,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { register } = useAuth();
  const { theme } = useTheme();
  const { t, changeLanguage, changeLanguageLocal } = useLocalization();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Fix для мобільних: виправляємо пустий простір після закриття клавіатури
  useEffect(() => {
    if (!isMobile) return;

    const handleResize = () => {
      // Прокручуємо до верху при зміні розміру (коли клавіатура закривається)
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
        // Також прокручуємо body, якщо потрібно
        if (document.body) {
          document.body.scrollTop = 0;
        }
      }, 150);
    };

    const handleBlur = (e) => {
      // Коли input втрачає фокус (клавіатура закривається)
      if (e.target.tagName === 'INPUT') {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'instant' });
          if (document.body) {
            document.body.scrollTop = 0;
          }
        }, 150);
      }
    };

    // Використовуємо visualViewport API, якщо доступний (для кращої підтримки)
    let visualViewportHandler = null;
    if (window.visualViewport) {
      visualViewportHandler = () => {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'instant' });
        }, 150);
      };
      window.visualViewport.addEventListener('resize', visualViewportHandler);
    }

    window.addEventListener('resize', handleResize);
    document.addEventListener('blur', handleBlur, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('blur', handleBlur, true);
      if (visualViewportHandler && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', visualViewportHandler);
      }
    };
  }, [isMobile]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Очищаем ошибку для этого поля
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = t("emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("emailInvalid");
    }

    if (!formData.password.trim()) {
      newErrors.password = t("passwordRequired");
    } else if (
      formData.password.length < 8 ||
      !/[a-zA-Z]/.test(formData.password) ||
      !/\d/.test(formData.password)
    ) {
      newErrors.password = t("passwordMinLength");
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = t("confirmPasswordRequired");
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t("passwordsDontMatch");
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = t("termsError");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      const result = await register(
        "Nachmed.AI",
        formData.email.trim(),
        formData.password,
        formData.confirmPassword,
      );

      // On success, either we're authenticated (tokens returned) or need verification
      if (result.success && result.authenticated) {
        analyticsService.logSignUp('web_successful_email_registration');
        const lang = localStorage.getItem("language");
        if (lang) {
          // Now authenticated, sync language to backend
          await changeLanguage(lang);
        }
        // Set welcome flag so ChatScreen shows the welcome modal once
        try { localStorage.setItem("show_welcome_after_register", "1"); } catch (_) {}
        navigate("/");
      } else if (result.success) {
        const lang = localStorage.getItem("language");
        if (lang) {
          // During registration we may not be authenticated yet; set language locally
          changeLanguageLocal(lang);
        }
        // No tokens returned – redirect to email verification flow
        try { localStorage.setItem("pendingVerificationEmail", formData.email.trim()); } catch (_) {}
        navigate("/verify-email", { state: { email: formData.email.trim() } });
      }

      if (result && result.status === 409) {
        analyticsService.logError('registration_failed', 'email_already_exists');
        setErrors({ email: t("alreadyExist") || t("registrationFailed") });
      } else if (result.error && result.error.includes("already registered")) {
        analyticsService.logError('registration_failed', 'email_already_exists');
        setErrors({ email: t("alreadyExist") || t("registrationFailed") });
      }
      if (result.error && result.error.includes("Network Error")) {
        analyticsService.logError('registration_failed', 'network_error');
        setErrors({ general: t("badGateway") || t("registrationFailed") });
      }
    } catch (error) {
      analyticsService.logError('registration_failed', 'exception');
      setErrors({ general: t("registrationFailed") });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleRegister();
    }
  };

  const containerStyle = {
    minHeight: isMobile ? "100dvh" : "100vh", // dvh для мобільних - враховує зміни viewport
    height: isMobile ? "100dvh" : "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: theme.background,
    padding: isMobile ? "12px 0" : "32px 0",
    boxSizing: "border-box",
    overflowY: isMobile ? "auto" : "visible",
    WebkitOverflowScrolling: "touch",
  };
  const formStyle = {
    width: isMobile ? "96vw" : 400,
    maxWidth: 400,
    background: theme.surface,
    borderRadius: isMobile ? 8 : 16,
    boxShadow: theme.isDark ? "0 2px 8px #0005" : "0 2px 8px #0002",
    padding: isMobile ? "20px 16px" : "32px",
    boxSizing: "border-box",
  };

  const titleStyles = {
    fontSize: isMobile ? "22px" : "26px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: isMobile ? "20px" : "32px",
    color: theme.text,
  };

  const formStyles = {
    display: "flex",
    flexDirection: "column",
    gap: isMobile ? "16px" : "20px",
    marginBottom: isMobile ? "16px" : "24px",
  };

  const linkStyles = {
    textAlign: "center",
    color: theme.primary,
    cursor: "pointer",
    textDecoration: "none",
    fontSize: "16px",
  };

  const errorStyles = {
    color: theme.error,
    fontSize: "14px",
    textAlign: "center",
    marginBottom: "16px",
  };

  const checkboxStyles = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "8px",
    color: theme.text,
    fontSize: "14px",
  };

  const checkboxInputStyles = {
    width: "16px",
    height: "16px",
    cursor: "pointer",
  };

  const checkboxErrorStyles = {
    color: theme.error,
    fontSize: "12px",
    marginTop: "4px",
  };

  const linkTextStyles = {
    color: theme.primary,
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  };

  const handleTermsClick = (e) => {
    e.preventDefault();
    window.open("https://nachmed.ai/policy", "_blank", "noopener,noreferrer");
  };

  const isFormValid =
    formData.email &&
    formData.password &&
    formData.confirmPassword &&
    formData.acceptTerms;

  const logoStyles = {
    display: "block",
    margin: isMobile ? "0 auto 12px auto" : "0 auto 24px auto",
    width: isMobile ? "200px" : "250px",
    height: "auto",
  };
  const isDark = theme.isDark;
  const logoToShow = isDark ? logoLight : logo;

  return (
    <div style={containerStyle}>
      <div style={formStyle}>
        <img src={logoToShow} alt="Logo" style={logoStyles} />
        <h1 style={titleStyles}>{t("createAccount")}</h1>

        {errors.general && <div style={errorStyles}>{errors.general}</div>}

        <div style={formStyles}>
          <Input
            label={t("email")}
            type="email"
            placeholder={t("email")}
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            onKeyPress={handleKeyPress}
            error={errors.email}
            required
            autoComplete="email"
          />

          <Input
            label={t("password")}
            type="password"
            placeholder={t("password")}
            value={formData.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            onKeyPress={handleKeyPress}
            error={errors.password}
            required
            autoComplete="new-password"
          />

          <Input
            label={t("confirmPassword")}
            type="password"
            placeholder={t("confirmPassword")}
            value={formData.confirmPassword}
            onChange={(e) =>
              handleInputChange("confirmPassword", e.target.value)
            }
            onKeyPress={handleKeyPress}
            error={errors.confirmPassword}
            required
            autoComplete="new-password"
          />

          <div style={checkboxStyles}>
            <input
              type="checkbox"
              checked={formData.acceptTerms}
              onChange={(e) => handleInputChange("acceptTerms", e.target.checked)}
              style={checkboxInputStyles}
            />
            <label>
              {t("acceptTerms")} {" "}
              <a
                href="https://nachmed.ai/policy"
                onClick={handleTermsClick}
                style={linkTextStyles}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("termsLink")}
              </a>
            </label>
          </div>
          {errors.acceptTerms && (
            <div style={checkboxErrorStyles}>{errors.acceptTerms}</div>
          )}

          <Button
            className="register-button"
            onClick={handleRegister}
            loading={loading}
            disabled={!isFormValid}
            size={isMobile ? "small" : "large"}
          >
            {t("register")}
          </Button>
        </div>

        <div style={linkStyles} onClick={() => navigate("/login")}>
          {t("alreadyHaveAccount")} <strong>{t("login")}</strong>
        </div>
      </div>
    </div>
  );
};

export default RegisterScreen;
